import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../utils/axiosSetup.js';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Swal from 'sweetalert2';
import { closeModal } from '../../../action/modalbehaviour.js';


const AddProject = ({list}) => {
    const [channels, setChannel] = useState([]);
    const [clients, setClients] = useState([]);
    const [teamLead, setTeamLead] = useState([]);
    const [selectedChannel, setSelectedChannel] = useState('');

    // VALIDATION RULES
    const schema = Yup.object().shape({
        channel_id: Yup.string().required('channel is a required field'),
        client_id: Yup.string().required('client is a required field'),
        title: Yup.string().required('name is a required field'),
        team_lead_id: Yup.string().required('team lead is a required field'),
        start_date: Yup.string().required(),
        amount: Yup.string().when('channel_id', {
            is: value => value !== 'Upwork',
            then: () => Yup.number().required('budget is a required field').typeError('budget must be a number')
        }),
        description: Yup.string().required()
    });

    const { register, handleSubmit, formState: { errors }, reset } = useForm({
        mode: 'all',
        resolver: yupResolver(schema)
    });
    
    const onSubmit = async (data) => {
        const formData = new FormData();
        Object.keys(data).forEach(key => {
            if (key === 'files') {
                Array.from(data[key]).forEach((file, index) => {
                    formData.append(`${key}[${index}]`, file);
                });
            }
            else {
                formData.append(key, data[key]);
            }
        });

        axiosInstance.post(`${process.env.REACT_APP_API_URL}/projects`, formData)
            .then(response => {
                Swal.fire({ icon:"success", text:response.data.message});
                reset();
                closeModal("add_project");
                list();
            })
            .catch(({response:{data}})=>{
                Swal.fire({text:data.message, icon:"error"});
            });
    }

    const fetchChannels = async () => {
        axiosInstance.get(`${process.env.REACT_APP_API_URL}/channels`)
            .then(response => {
                setChannel(response.data.data);
            })
            .catch(error => {
                console.error('Error fetching channels', error);
                setChannel([]);
            });
    };
    
    const fetchClients = async () => {
        axiosInstance.get(`${process.env.REACT_APP_API_URL}/clients`)
            .then(response => {
                setClients(response.data.data);
            })
            .catch(error => {
                console.error('Error fetching clients', error);
                setClients([]);
            });
    }

    const fetchTeamLead = async () => {
        axiosInstance.get(`${process.env.REACT_APP_API_URL}/users?users=teamleads`)
            .then(response => {
                setTeamLead(response.data.data);
            })
            .catch(error => {
                console.error('Error fetching teamleads', error);
                setTeamLead([]);
            });
    };

    useEffect(() => {
        fetchChannels();
        fetchClients();
        fetchTeamLead();

        const modalElement = document.getElementById('add_project');

        const handleClose = () => {
            reset();
            setSelectedChannel('');
        };

        if (modalElement) {
            modalElement.addEventListener('hidden.bs.modal', handleClose);
        }
        return () => {
            if (modalElement) {
                modalElement.removeEventListener('hidden.bs.modal', handleClose);
            }
        };
    }, [reset]);

    // Filter clients based on selected channel
    const filteredClients = selectedChannel 
        ? clients.filter(client => client.channel_name === selectedChannel) 
        : [];

    return (
        <div id="add_project" className="modal custom-modal fade" role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Add Project</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div className="modal-body">
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Channel <span className="text-danger">*</span></label>
                                        <select className="form-control" {...register('channel_id')}
                                                onChange={(event) => {setSelectedChannel(event.target.value)}}>
                                            <option value="">Select Channel</option>
                                            {channels.map(channel => (
                                                <option key={channel.id} value={channel.name}>{channel.name}</option>
                                            ))}
                                        </select>
                                        <div className="text-sm text-danger">
                                            {errors.channel_id && <p>{errors.channel_id.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Client <span className="text-danger">*</span></label>
                                        <select className="form-control" {...register('client_id')}>
                                            <option value="">Select Client</option>
                                            {filteredClients.map(client => (
                                                <option key={client.id} value={client.id}>{client.name}</option>
                                            ))}
                                        </select>
                                        <div className="text-sm text-danger">
                                            {errors.client_id && <p>{errors.client_id.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Project Name <span className="text-danger">*</span></label>
                                        <input className="form-control" type="text" {...register('title')} />
                                        <div className="text-sm text-danger">
                                            {errors.title && <p>{errors.title.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Team Lead <span className="text-danger">*</span></label>
                                        <select className="form-control" {...register('team_lead_id')}>
                                            <option value="">Select Team Lead</option>
                                            {teamLead.map(tl => (
                                                <option key={tl.id} value={tl.id}>{tl.name}</option>
                                            ))}
                                        </select>
                                        <div className="text-sm text-danger">
                                            {errors.team_lead_id && <p>{errors.team_lead_id.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Start Date <span className="text-danger">*</span></label>
                                        <input className="form-control" type="date" {...register('start_date')} />
                                        <div className="text-sm text-danger">
                                            {errors.start_date && <p>{errors.start_date.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6" style={{display: selectedChannel !== 'Upwork' ? 'block' : 'none'}}>
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Budget <span className="text-danger">*</span></label>
                                        <input className="form-control" type="text" {...register('amount')} />
                                        <div className="text-sm text-danger">
                                            {errors.amount && <p>{errors.amount.message}</p>}
                                        </div>
                                    </div>
                                </div>                                
                                <div className="col-sm-12">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Description <span className="text-danger">*</span></label>
                                        <textarea className="form-control" {...register('description')}  rows={3}></textarea>
                                        <div className="text-sm text-danger">
                                            {errors.description && <p>{errors.description.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Files</label>
                                        <input className="form-control" type="file" {...register('files')} multiple />                                        
                                    </div>
                                </div>                                
                            </div>
                            <div className="submit-section">
                                <button type="submit" className="btn btn-primary submit-btn">SUBMIT</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddProject